import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="关于我们_韩漫漫画网_免费韩漫大全_韩国漫画在线阅读平台"
        description="韩漫漫画网关于我们页面，了解韩漫漫画网的品牌故事、发展历程、用户口碑和联系方式。免费韩漫大全平台，专注提供好看的韩国漫画在线阅读服务。"
        keywords="韩漫漫画网,关于我们,免费韩漫大全,韩国漫画,联系方式,品牌介绍"
        canonicalUrl="https://www.hanmancomic.com/about"
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 韩漫漫画网',
          description: '了解韩漫漫画网的品牌故事与联系方式',
          url: 'https://www.hanmancomic.com/about',
        }}
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: '韩漫漫画网',
          url: 'https://www.hanmancomic.com',
          description: '免费韩漫大全，好看的韩国漫画在线阅读平台',
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'contact@hanmancomic.com',
            contactType: 'customer service',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />

      <Navbar />
      <Breadcrumb items={[{ label: '关于我们' }]} />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20illustration%20with%20soft%20amber%20and%20coral%20gradients%2C%20gentle%20flowing%20shapes%2C%20professional%20clean%20atmosphere%2C%20Korean%20aesthetic%20inspired%2C%20calm%20and%20welcoming%20feeling%2C%20digital%20art%20style&width=1600&height=600&seq=about-hero-01&orientation=landscape"
            alt="关于韩漫漫画网"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                关于我们
              </h1>
              <p className="text-base text-white/80 max-w-xl mx-auto">
                了解韩漫漫画网的故事与使命
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              品牌介绍
            </h2>
            <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
              <p>
                <strong className="text-foreground-950">韩漫漫画网</strong>成立于2020年，是国内领先的<strong>韩国漫画在线阅读</strong>平台。我们致力于为广大漫画爱好者提供最全面、最优质的<strong>免费韩漫大全</strong>资源，让每一位读者都能在这里找到自己喜欢的<strong>好看韩国漫画</strong>。
              </p>
              <p>
                经过多年的积累与发展，韩漫漫画网已经收录了数百部精品韩国漫画作品，涵盖浪漫爱情、动作冒险、奇幻玄幻、悬疑惊悚、喜剧日常、校园青春、古风历史、都市职场等八大类型。无论是追求甜蜜恋爱故事的读者，还是热爱热血战斗场面的粉丝，都能在我们的平台上找到心仪的作品。
              </p>
              <p>
                我们的漫画网址简洁易记，网站界面清新优雅，采用先进的响应式设计技术，完美适配桌面端、平板和手机等各类设备。无论您身处何地，都能通过<strong>韩漫漫画网</strong>畅享流畅的<strong>韩漫在线阅读</strong>体验。
              </p>
              <p>
                韩漫漫画网始终坚持以用户体验为核心，不断优化阅读功能、提升加载速度、丰富内容资源。我们与多家韩国漫画版权方保持着良好的合作关系，确保为读者提供正版、高清、稳定更新的漫画内容。在这里，您可以放心地沉浸在韩国漫画的精彩世界中。
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的使命
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '收录数百部精品韩国漫画，覆盖八大类型，持续更新扩充，确保读者拥有丰富的选择空间。' },
                { icon: 'ri-shield-check-line', title: '品质保障', desc: '坚持正版高清内容，优化阅读体验，提供流畅的分页加载和舒适的排版设计。' },
                { icon: 'ri-heart-line', title: '用户至上', desc: '以读者需求为导向，持续优化产品功能，提供个性化推荐和便捷的阅读管理工具。' },
              ].map((item, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 max-w-4xl mx-auto">
            {testimonials.map((item, index) => (
              <div key={index} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }, (_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">{item.content}</p>
                <p className="text-xs text-foreground-500 font-medium">— {item.name}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-600">contact@hanmancomic.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-3">
                  <i className="ri-wechat-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-600">韩漫漫画网</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-3">
                  <i className="ri-weibo-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">官方微博</h3>
                <p className="text-xs text-foreground-600">@韩漫漫画网官方</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
            常见问题
          </h2>
          <div className="max-w-2xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                      faqOpen === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {faqOpen === index && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}